import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

// Pile des modales ouvertes : Échap ne ferme que celle du dessus
const stack = [];

/**
 * Modale unique de l'application : bouton ✕ en haut à droite, fermeture par Échap,
 * hauteur max = viewport avec défilement interne (DB-4495). Pas de fermeture au clic
 * sur le fond : une saisie en cours ne doit pas se perdre sur un clic à côté.
 *
 * title    : titre (nœud) ; subtitle : ligne grise sous le titre
 * footer   : boutons d'action, alignés à droite
 * onSubmit : si fourni, la modale est un <form> (Entrée valide, footer inclus)
 * width    : largeur max (défaut 560px)
 */
export default function Modal({ title, subtitle, onClose, onSubmit, footer, width = 560, titleColor = '#232853', children }) {
  const id = useRef({});
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    const me = id.current;
    stack.push(me);
    const onKey = e => {
      if (e.key === 'Escape' && stack[stack.length - 1] === me) {
        e.stopPropagation();
        onCloseRef.current?.();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      stack.splice(stack.indexOf(me), 1);
    };
  }, []);

  const Card = onSubmit ? 'form' : 'div';
  const submit = onSubmit ? e => { e.preventDefault(); onSubmit(e); } : undefined;

  return createPortal(
    <div style={styles.overlay}>
      <Card style={{ ...styles.card, width: `${width}px` }} onSubmit={submit} role="dialog" aria-modal="true">
        <button type="button" onClick={onClose} aria-label="Fermer" title="Fermer (Échap)" style={styles.close}
                className="ui-modal-close">
          ✕
        </button>
        {title && <h3 style={{ ...styles.title, color: titleColor }}>{title}</h3>}
        {subtitle && <div style={styles.subtitle}>{subtitle}</div>}
        <div style={{ marginTop: title || subtitle ? '1rem' : 0 }}>{children}</div>
        {footer && <div style={styles.footer}>{footer}</div>}
      </Card>
    </div>,
    document.body
  );
}

const styles = {
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50 },
  card: {
    position: 'relative', background: '#ffffff', borderRadius: '12px', padding: '2rem',
    maxWidth: 'calc(100vw - 2rem)', maxHeight: 'calc(100vh - 2rem)', overflowY: 'auto',
    boxSizing: 'border-box', boxShadow: '0 20px 60px rgba(0,0,0,0.2)', color: '#444444',
  },
  close: {
    position: 'absolute', top: '0.9rem', right: '0.9rem', width: '2rem', height: '2rem',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    border: 'none', borderRadius: '6px', cursor: 'pointer',   // fond + survol : .ui-modal-close
    color: '#6b7280', fontSize: '1.05rem', lineHeight: 1,
  },
  title: { margin: 0, paddingRight: '2.5rem', fontSize: '1.15rem' },
  subtitle: { color: '#8a8f99', fontSize: '0.82rem', marginTop: '0.3rem', paddingRight: '2.5rem' },
  footer: { display: 'flex', gap: '0.75rem', justifyContent: 'flex-end', marginTop: '1.5rem', flexWrap: 'wrap' },
};
